import React, { useState, useEffect } from 'react';
import { Clock } from 'lucide-react';
import * as dateUtils from '../utils/dateUtils';

function TimeClock({ userInfo }) {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [location, setLocation] = useState(null);
  const [error, setError] = useState(null);
  const [todaysPunches, setTodaysPunches] = useState([]);
  const [showLocationPrompt, setShowLocationPrompt] = useState(true);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingPunchType, setPendingPunchType] = useState(null);
  const [message, setMessage] = useState(null);

  // 1秒ごとに時刻を更新
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // コンポーネントマウント時に実行
  useEffect(() => {
    console.log('TimeClock コンポーネントがマウントされました');
    console.log('現在のUTC時間:', new Date().toISOString());
    console.log('現在の日本時間:', dateUtils.getCurrentJSTDate(), dateUtils.getCurrentJSTTime(true));
    
    // 今日の打刻記録を取得
    fetchTodaysPunches();
    
    // 1分ごとに打刻記録を更新
    const interval = setInterval(() => {
      console.log('定期更新: 今日の打刻記録を取得します');
      fetchTodaysPunches();
    }, 60 * 1000);
    
    return () => clearInterval(interval);
  }, []);

  // 位置情報を取得
  const getLocation = async () => {
    try {
      if (!navigator.geolocation) {
        throw new Error('お使いのブラウザは位置情報をサポートしていません');
      }

      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 5000,
          maximumAge: 0
        });
      });

      const { latitude, longitude } = position.coords;
      setLocation({ latitude, longitude });
      setShowLocationPrompt(false);
      setError(null);
      return { latitude, longitude };
    } catch (err) {
      console.error('位置情報の取得に失敗しました:', err);
      setError('位置情報の取得に失敗しました: ' + err.message);
      return null;
    }
  };

  // 打刻確認ダイアログを表示
  const confirmPunch = async (type) => {
    try {
      // 位置情報を取得
      const locationData = await getLocation();
      if (!locationData && type !== 'break') {
        setError('位置情報の取得に失敗しました。位置情報を許可してください。');
        return;
      }

      // 確認ダイアログを表示
      setPendingPunchType(type);
      setShowConfirmDialog(true);
    } catch (err) {
      console.error('打刻確認エラー:', err);
      setError('打刻確認に失敗しました: ' + err.message);
    }
  };

  // 打刻処理
  const handlePunch = async (type) => {
    try {
      setShowConfirmDialog(false);
      
      // 打刻タイプの検証
      if (!type || (type !== 'in' && type !== 'out')) {
        setError('無効な打刻タイプです: ' + type);
        return;
      }
      
      console.log('打刻処理を開始します:', { type });
      
      // 現在の日本時間
      const currentJSTTime = dateUtils.getCurrentJSTTime(true);
      console.log('打刻時の日本時間:', currentJSTTime);
      
      // 簡素化したリクエスト
      const response = await fetch('/api/time-records', {
        method: 'POST',
        headers: {
          'Authorization': localStorage.getItem('token'),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          type: type,
          note: `${type === 'in' ? '出勤' : '退勤'} - ${currentJSTTime}`
        })
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('APIレスポンスエラー:', response.status, errorText);
        throw new Error('打刻に失敗しました: ' + errorText);
      }
      
      const result = await response.json();
      console.log('打刻結果:', result);
      
      // 成功メッセージを表示
      setMessage(`${type === 'in' ? '出勤' : '退勤'}を記録しました`);
      
      // 今日の打刻記録を再取得
      fetchTodaysPunches();
      
      // 3秒後にメッセージをクリア
      setTimeout(() => {
        setMessage(null);
      }, 3000);
    } catch (err) {
      console.error('打刻エラー:', err);
      setError('打刻に失敗しました: ' + err.message);
      
      // 3秒後にエラーメッセージをクリア
      setTimeout(() => {
        setError(null);
      }, 3000);
    }
  };

  // 今日の打刻記録を取得
  const fetchTodaysPunches = async () => {
    try {
      // 現在の日本時間の日付
      const today = dateUtils.getCurrentJSTDate();
      console.log('今日の日付（JST）:', today);
      console.log('ユーザー情報:', userInfo);
      
      // キャッシュを防止するためのパラメータを追加
      const timestamp = new Date().getTime();
      const url = `/api/time-records?date=${today}&_=${timestamp}`;
      console.log('APIリクエストURL:', url);
      
      const response = await fetch(url, {
        headers: {
          'Authorization': localStorage.getItem('token'),
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('APIレスポンスエラー:', response.status, errorText);
        throw new Error('打刻記録の取得に失敗しました');
      }
      
      const data = await response.json();
      console.log('取得した打刻記録 (生データ):', data);
      console.log('取得した打刻記録の数:', data.length);
      
      // 日本時間に変換して表示用に整形
      const formattedPunches = data.map(record => ({
        ...record,
        jstTime: dateUtils.formatTimeToJST(record.recorded_at),
        jstDate: dateUtils.formatDateToJST(record.recorded_at)
      }));
      
      console.log('整形した打刻記録:', formattedPunches);
      console.log('出勤記録:', formattedPunches.find(record => record.record_type === 'in'));
      console.log('退勤記録:', formattedPunches.find(record => record.record_type === 'out'));
      
      setTodaysPunches(formattedPunches);
      
      // hasClockedIn と hasClockedOut の結果を確認
      setTimeout(() => {
        console.log('hasClockedIn():', hasClockedIn());
        console.log('hasClockedOut():', hasClockedOut());
        console.log('todaysPunches:', todaysPunches);
      }, 100);
    } catch (err) {
      console.error('打刻記録取得エラー:', err);
      setError('打刻記録の取得に失敗しました: ' + err.message);
    }
  };

  // 現在時刻を日本時間で表示
  const formatCurrentTime = () => {
    // ブラウザのローカル時間ではなく、日本時間を使用
    return dateUtils.getCurrentJSTTime(true);
  };

  // 今日の出勤記録を取得
  const getClockInRecord = () => {
    console.log('getClockInRecord - todaysPunches:', todaysPunches);
    // 'in' または 'clock_in' タイプの記録を検索
    return todaysPunches.find(record => record.record_type === 'in' || record.record_type === 'clock_in');
  };

  // 今日の退勤記録を取得
  const getClockOutRecord = () => {
    console.log('getClockOutRecord - todaysPunches:', todaysPunches);
    // 'out' または 'clock_out' タイプの記録を検索
    return todaysPunches.find(record => record.record_type === 'out' || record.record_type === 'clock_out');
  };

  // 出勤済みかどうか
  const hasClockedIn = () => {
    const record = getClockInRecord();
    console.log('hasClockedIn - record:', record);
    return !!record;
  };

  // 退勤済みかどうか
  const hasClockedOut = () => {
    const record = getClockOutRecord();
    console.log('hasClockedOut - record:', record);
    return !!record;
  };

  return (
    <div className="p-4 bg-white rounded-lg shadow">
      <h2 className="text-xl font-bold mb-4 flex items-center">
        <Clock className="mr-2" /> 勤怠打刻
      </h2>
      
      {/* 現在時刻 */}
      <div className="text-center mb-6">
        <div className="text-3xl font-bold">{formatCurrentTime()}</div>
        <div className="text-gray-500">{dateUtils.getCurrentJSTDate()}</div>
      </div>
      
      {/* メッセージ表示 */}
      {message && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
          {message}
        </div>
      )}
      
      {/* エラーメッセージ */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}
      
      {/* 位置情報許可プロンプト */}
      {showLocationPrompt && (
        <div className="bg-blue-100 border border-blue-400 text-blue-700 px-4 py-3 rounded mb-4">
          <p>打刻には位置情報が必要です。</p>
          <button 
            onClick={getLocation}
            className="mt-2 bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
          >
            位置情報を許可する
          </button>
        </div>
      )}
      
      {/* 打刻ボタン */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <button 
          onClick={() => confirmPunch('in')}
          disabled={showLocationPrompt}
          className={`py-3 px-4 rounded-lg font-bold text-white ${
            showLocationPrompt 
              ? 'bg-gray-400 cursor-not-allowed' 
              : 'bg-green-500 hover:bg-green-600'
          }`}
        >
          出勤
        </button>
        <button 
          onClick={() => confirmPunch('out')}
          disabled={showLocationPrompt}
          className={`py-3 px-4 rounded-lg font-bold text-white ${
            showLocationPrompt 
              ? 'bg-gray-400 cursor-not-allowed' 
              : 'bg-red-500 hover:bg-red-600'
          }`}
        >
          退勤
        </button>
      </div>
      
      {/* 確認ダイアログ */}
      {showConfirmDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md w-full">
            <h3 className="text-xl font-bold mb-4">
              {pendingPunchType === 'in' ? '出勤' : '退勤'}確認
            </h3>
            <p className="mb-4">
              {pendingPunchType === 'in' ? '出勤' : '退勤'}を記録しますか？
            </p>
            <div className="flex justify-end space-x-2">
              <button 
                onClick={() => setShowConfirmDialog(false)}
                className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded"
              >
                キャンセル
              </button>
              <button 
                onClick={() => handlePunch(pendingPunchType)}
                className={`text-white font-bold py-2 px-4 rounded ${
                  pendingPunchType === 'in' 
                    ? 'bg-green-500 hover:bg-green-600' 
                    : 'bg-red-500 hover:bg-red-600'
                }`}
              >
                確認
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* 今日の打刻記録 */}
      <div className="mt-6">
        <h3 className="text-lg font-semibold mb-2">今日の打刻記録</h3>
        <div className="bg-gray-100 p-4 rounded">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-gray-600">出勤時間</p>
              <p className="font-bold">
                {getClockInRecord() 
                  ? getClockInRecord().jstTime
                  : '未打刻'}
              </p>
            </div>
            <div>
              <p className="text-gray-600">退勤時間</p>
              <p className="font-bold">
                {getClockOutRecord() 
                  ? getClockOutRecord().jstTime
                  : '未打刻'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TimeClock;
